import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
} from 'react'
import { i18n, useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button } from './Button'
import { controlClass, pickerClass } from './controlClass'
import { Popover } from './Popover'

/*
 * Date and date-time field used for every `type="date"` / `type="datetime-local"`
 * Input. The value contract is the native one ("YYYY-MM-DD", "YYYY-MM-DDTHH:mm")
 * so forms and filters keep working unchanged, but the field is numeric
 * ("27.09.2026 15:10"): digits can be typed straight in (separators appear on
 * their own), or a day picked from the calendar.
 */

type Parts = { y: number; m: number; d: number; hh: number; mm: number }

const pad = (value: number, length = 2) => String(value).padStart(length, '0')
const daysIn = (y: number, m: number) => new Date(y, m, 0).getDate()
const compareDay = (a: Parts, b: Parts) => a.y - b.y || a.m - b.m || a.d - b.d
const sameDay = (a: Parts | null, b: Parts) => Boolean(a && compareDay(a, b) === 0)

function parseValue(value: string | undefined): Parts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(value ?? '')
  if (!match) return null
  return { y: +match[1], m: +match[2], d: +match[3], hh: match[4] ? +match[4] : 0, mm: match[5] ? +match[5] : 0 }
}

function toValue(parts: Parts, withTime: boolean) {
  const date = `${pad(parts.y, 4)}-${pad(parts.m)}-${pad(parts.d)}`
  return withTime ? `${date}T${pad(parts.hh)}:${pad(parts.mm)}` : date
}

function toText(parts: Parts | null, withTime: boolean) {
  if (!parts) return ''
  const date = `${pad(parts.d)}.${pad(parts.m)}.${pad(parts.y, 4)}`
  return withTime ? `${date} ${pad(parts.hh)}:${pad(parts.mm)}` : date
}

/** "27092026" → "27.09.2026"; the separators follow the digits as they are typed. */
function mask(digits: string) {
  const separators: Record<number, string> = { 2: '.', 4: '.', 8: ' ', 10: ':' }
  let out = ''
  for (let index = 0; index < digits.length; index++) out += (separators[index] ?? '') + digits[index]
  return out
}

function fromDigits(digits: string): Parts | null {
  if (digits.length !== 8 && digits.length !== 12) return null
  const d = +digits.slice(0, 2)
  const m = +digits.slice(2, 4)
  const y = +digits.slice(4, 8)
  const hh = digits.length === 12 ? +digits.slice(8, 10) : 0
  const mm = digits.length === 12 ? +digits.slice(10, 12) : 0
  if (m < 1 || m > 12 || y < 1900 || y > 2100 || d < 1 || d > daysIn(y, m) || hh > 23 || mm > 59) return null
  return { y, m, d, hh, mm }
}

const todayParts = (): Parts => {
  const now = new Date()
  return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate(), hh: now.getHours(), mm: now.getMinutes() }
}

/** Moves a day by `days`, crossing months and years. */
function shift(parts: Parts, days: number): Parts {
  const date = new Date(parts.y, parts.m - 1, parts.d + days)
  return { ...parts, y: date.getFullYear(), m: date.getMonth() + 1, d: date.getDate() }
}

/** Same day in another month, clamped to that month's length. */
function shiftMonth(parts: Parts, months: number): Parts {
  const index = parts.y * 12 + (parts.m - 1) + months
  const y = Math.floor(index / 12)
  const m = (index % 12) + 1
  return { ...parts, y, m, d: Math.min(parts.d, daysIn(y, m)) }
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

function names(kind: 'weekday' | 'month' | 'monthShort') {
  const make = (tag: string) =>
    kind === 'weekday'
      ? new Intl.DateTimeFormat(tag, { weekday: 'short' })
      : new Intl.DateTimeFormat(tag, { month: kind === 'month' ? 'long' : 'short' })
  let format: Intl.DateTimeFormat
  try {
    format = make(i18n.tag)
  } catch {
    format = make('en-GB')
  }
  // 1 Jan 2024 is a Monday: weeks start on Monday.
  return Array.from({ length: kind === 'weekday' ? 7 : 12 }, (_, index) =>
    capitalize(format.format(kind === 'weekday' ? new Date(2024, 0, 1 + index) : new Date(2024, index, 1)).replace('.', '')),
  )
}

type DateInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'size'> & {
  value?: string | number | readonly string[]
  type?: string
  /** Native-style change: `event.target.value` is "YYYY-MM-DD" / "YYYY-MM-DDTHH:mm" or "". */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  onValueChange?: (value: string) => void
  /** No field chrome, for inline filter chips. */
  bare?: boolean
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { value: rawValue, type, onChange, onValueChange, bare, className, min, max, disabled, readOnly, placeholder, name, onBlur, onKeyDown, ...rest },
  ref,
) {
  const t = useT()
  const withTime = type === 'datetime-local'
  const value = typeof rawValue === 'string' ? rawValue : ''
  const selected = parseValue(value)
  const minParts = parseValue(min === undefined ? undefined : String(min))
  const maxParts = parseValue(max === undefined ? undefined : String(max))
  const inRange = (parts: Parts) => (!minParts || compareDay(parts, minParts) >= 0) && (!maxParts || compareDay(parts, maxParts) <= 0)
  const inactive = disabled || readOnly

  // The typed text follows the value, except for the echo of our own commit:
  // a parent (e.g. a URL-backed filter) may re-render with it after the user typed on.
  const [text, setText] = useState(() => toText(selected, withTime))
  const [synced, setSynced] = useState(value)
  const [committed, setCommitted] = useState<string | null>(null)
  if (synced !== value) {
    setSynced(value)
    if (value !== committed) setText(toText(parseValue(value), withTime))
    setCommitted(null)
  }

  const wrapper = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const setRefs = (node: HTMLInputElement | null) => {
    inputRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }

  const commit = (next: string) => {
    if (next === value) return
    setCommitted(next)
    onValueChange?.(next)
    onChange?.({ target: { value: next, name }, currentTarget: { value: next, name } } as unknown as ChangeEvent<HTMLInputElement>)
  }

  const digits = text.replace(/\D/g, '')
  const full = withTime ? 12 : 8
  const typedParts = fromDigits(digits)
  const invalid = digits.length === full && !(typedParts && inRange(typedParts))

  const onType = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target
    const caretDigits = input.value.slice(0, input.selectionStart ?? input.value.length).replace(/\D/g, '').length
    const nextDigits = input.value.replace(/\D/g, '').slice(0, full)
    const next = mask(nextDigits)
    setText(next)
    // Keep the caret after the same digit once separators are re-inserted.
    requestAnimationFrame(() => {
      let position = 0
      for (let seen = 0; position < next.length && seen < caretDigits; position++) if (/\d/.test(next[position])) seen++
      input.setSelectionRange(position, position)
    })
    if (!nextDigits) return commit('')
    const parts = fromDigits(nextDigits)
    if (parts && nextDigits.length === full && inRange(parts)) commit(toValue(parts, withTime))
  }

  /** Blur / Enter: accept a complete date (a date-time without time keeps the previous time), else restore. */
  const settle = () => {
    if (!digits) {
      commit('')
      return setText('')
    }
    const parts = fromDigits(digits)
    if (parts && inRange(parts)) {
      if (withTime && digits.length === 8) Object.assign(parts, { hh: selected?.hh ?? 0, mm: selected?.mm ?? 0 })
      commit(toValue(parts, withTime))
      return setText(toText(parts, withTime))
    }
    setText(toText(selected, withTime))
  }

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = parseValue(event.clipboardData.getData('text').trim())
    if (!pasted) return
    event.preventDefault()
    setText(toText(pasted, withTime))
    if (inRange(pasted)) commit(toValue(pasted, withTime))
  }

  const pick = (parts: Parts) => {
    setText(toText(parts, withTime))
    commit(toValue(parts, withTime))
  }

  return (
    <div ref={wrapper} className={cn('min-w-0', bare ? 'inline-flex' : 'w-full')}>
      <Popover
        kind="dialog"
        className="w-[292px] p-3"
        trigger={({ open, toggle }) => (
          <div className="relative flex min-w-0 items-center">
            <input
              {...rest}
              ref={setRefs}
              name={name}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
              disabled={disabled}
              readOnly={readOnly}
              value={text}
              placeholder={placeholder ?? (withTime ? t('common.datePicker.placeholderTime') : t('common.datePicker.placeholder'))}
              aria-invalid={rest['aria-invalid'] ?? (invalid || undefined)}
              onChange={onType}
              onPaste={onPaste}
              onClick={() => !inactive && !open && toggle(true)}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                onKeyDown?.(event)
                if (event.key === 'Enter') settle()
                if (event.key === 'Escape' && open) {
                  event.stopPropagation()
                  toggle()
                }
                if (event.key === 'ArrowDown' && !inactive) {
                  event.preventDefault()
                  if (!open) toggle(true)
                  // Hand the keyboard to the calendar grid.
                  requestAnimationFrame(() => document.querySelector<HTMLElement>('[data-date-panel] [data-cursor="true"]')?.focus())
                }
              }}
              onBlur={(event: FocusEvent<HTMLInputElement>) => {
                onBlur?.(event)
                const next = event.relatedTarget as Node | null
                if (next && (wrapper.current?.contains(next) || (next as Element).closest?.('[data-date-panel]'))) return
                settle()
                if (open) toggle()
              }}
              className={cn(
                'num',
                bare ? 'w-[88px] bg-transparent text-[13px] text-fg outline-none' : cn(pickerClass(Boolean(selected), readOnly), 'h-[calc(var(--spacing)*9*var(--ui-size))] pr-9'),
                bare && withTime && 'w-[124px]',
                className,
              )}
            />
            {!inactive && (
              <button
                type="button"
                tabIndex={-1}
                aria-label={t('common.datePicker.open')}
                title={t('common.datePicker.open')}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  toggle()
                  inputRef.current?.focus({ preventScroll: true })
                }}
                className={cn(
                  'flex shrink-0 items-center justify-center rounded-[calc(7px*var(--radius-scale))] text-subtle transition-colors hover:text-fg',
                  bare ? 'ml-1 size-5' : 'absolute right-1.5 size-6.5 hover:bg-hover',
                  open && 'text-accent',
                )}
              >
                <CalendarDays className="size-4" strokeWidth={1.85} />
              </button>
            )}
          </div>
        )}
      >
        {(close) => (
          <CalendarPanel
            selected={selected}
            withTime={withTime}
            inRange={inRange}
            onPick={(parts) => {
              pick(parts)
              if (!withTime) {
                close()
                inputRef.current?.focus({ preventScroll: true })
              }
            }}
            onClear={() => {
              setText('')
              commit('')
              close()
            }}
            onDone={() => {
              close()
              inputRef.current?.focus({ preventScroll: true })
            }}
          />
        )}
      </Popover>
    </div>
  )
})

function CalendarPanel({
  selected,
  withTime,
  inRange,
  onPick,
  onClear,
  onDone,
}: {
  selected: Parts | null
  withTime: boolean
  inRange: (parts: Parts) => boolean
  onPick: (parts: Parts) => void
  onClear: () => void
  onDone: () => void
}) {
  const t = useT()
  const today = todayParts()
  const [cursor, setCursor] = useState<Parts>(() => selected ?? { ...today, hh: 0, mm: 0 })
  const [mode, setMode] = useState<'days' | 'months' | 'years'>('days')
  const grid = useRef<HTMLDivElement>(null)

  // Typing a date in the field moves the calendar to it.
  const selectedKey = selected ? toValue(selected, false) : ''
  const [seen, setSeen] = useState(selectedKey)
  if (seen !== selectedKey) {
    setSeen(selectedKey)
    if (selected) setCursor(selected)
  }

  // Arrow keys move focus with the cursor, but only once the grid has the keyboard.
  useEffect(() => {
    if (grid.current?.contains(document.activeElement)) grid.current.querySelector<HTMLElement>('[data-cursor="true"]')?.focus()
  }, [cursor])

  const weekdays = names('weekday')
  const months = names('month')
  const monthsShort = names('monthShort')
  const time = { hh: selected?.hh ?? cursor.hh, mm: selected?.mm ?? cursor.mm }
  const withChosenTime = (parts: Parts): Parts => ({ ...parts, ...time })

  const first = new Date(cursor.y, cursor.m - 1, 1)
  const offset = (first.getDay() + 6) % 7
  const cells = Array.from({ length: 42 }, (_, index) => shift({ ...cursor, d: 1 }, index - offset))

  const onGridKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, () => Parts> = {
      ArrowLeft: () => shift(cursor, -1),
      ArrowRight: () => shift(cursor, 1),
      ArrowUp: () => shift(cursor, -7),
      ArrowDown: () => shift(cursor, 7),
      PageUp: () => shiftMonth(cursor, event.shiftKey ? -12 : -1),
      PageDown: () => shiftMonth(cursor, event.shiftKey ? 12 : 1),
      Home: () => shift(cursor, -((new Date(cursor.y, cursor.m - 1, cursor.d).getDay() + 6) % 7)),
      End: () => shift(cursor, 6 - ((new Date(cursor.y, cursor.m - 1, cursor.d).getDay() + 6) % 7)),
    }
    if (moves[event.key]) {
      event.preventDefault()
      setCursor(moves[event.key]())
    } else if ((event.key === 'Enter' || event.key === ' ') && inRange(cursor)) {
      event.preventDefault()
      onPick(withChosenTime(cursor))
    }
  }

  const step = (direction: -1 | 1) =>
    setCursor((current) => (mode === 'days' ? shiftMonth(current, direction) : shiftMonth(current, direction * (mode === 'months' ? 12 : 144))))
  const yearStart = Math.floor(cursor.y / 12) * 12
  const title = mode === 'days' ? `${months[cursor.m - 1]} ${cursor.y}` : mode === 'months' ? String(cursor.y) : `${yearStart}–${yearStart + 11}`
  const setTime = (patch: Partial<Pick<Parts, 'hh' | 'mm'>>) => onPick({ ...(selected ?? cursor), ...time, ...patch })

  return (
    <div data-date-panel className="flex flex-col gap-2.5" onMouseDown={(event) => event.target instanceof HTMLInputElement || event.preventDefault()}>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => step(-1)} aria-label={t('common.datePicker.previous')} className="flex size-8 items-center justify-center rounded-[calc(9px*var(--radius-scale))] text-muted hover:bg-hover hover:text-fg">
          <ChevronLeft className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => setMode(mode === 'days' ? 'months' : mode === 'months' ? 'years' : 'days')}
          aria-label={t('common.datePicker.chooseMonth')}
          className="h-8 flex-1 rounded-[calc(9px*var(--radius-scale))] text-[length:calc(13.5px*var(--text-scale))] font-semibold text-fg hover:bg-hover"
        >
          {title}
        </button>
        <button type="button" onClick={() => step(1)} aria-label={t('common.datePicker.next')} className="flex size-8 items-center justify-center rounded-[calc(9px*var(--radius-scale))] text-muted hover:bg-hover hover:text-fg">
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div key={`${mode}:${mode === 'days' ? `${cursor.y}-${cursor.m}` : mode === 'months' ? cursor.y : yearStart}`} className="animate-fade-in">
        {mode === 'days' && (
          <div ref={grid} role="grid" aria-label={title} onKeyDown={onGridKey}>
            <div role="row" className="mb-1 grid grid-cols-7">
              {weekdays.map((day, index) => (
                <span key={day} role="columnheader" className={cn('text-center text-[length:calc(11.5px*var(--text-scale))] font-medium text-subtle', index > 4 && 'text-danger/70')}>
                  {day}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
              {cells.map((cell) => {
                const outside = cell.m !== cursor.m
                const chosen = sameDay(selected, cell)
                const isToday = sameDay(today, cell)
                const isCursor = compareDay(cell, cursor) === 0
                const allowed = inRange(cell)
                return (
                  <button
                    key={toValue(cell, false)}
                    type="button"
                    role="gridcell"
                    tabIndex={isCursor ? 0 : -1}
                    data-cursor={isCursor}
                    aria-selected={chosen}
                    aria-current={isToday ? 'date' : undefined}
                    aria-label={toText(cell, false)}
                    disabled={!allowed}
                    onClick={() => onPick(withChosenTime(cell))}
                    className={cn(
                      'num relative mx-auto flex size-9 items-center justify-center rounded-[calc(9px*var(--radius-scale))] text-[length:calc(13px*var(--text-scale))] transition-colors duration-100',
                      'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
                      chosen ? 'bg-accent font-semibold text-accent-fg' : outside ? 'text-subtle hover:bg-hover' : 'text-fg hover:bg-hover',
                      isToday && !chosen && 'font-semibold text-accent ring-1 ring-inset ring-accent/40',
                      !allowed && 'cursor-not-allowed opacity-30 hover:bg-transparent',
                    )}
                  >
                    {cell.d}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {mode === 'months' && (
          <div className="grid grid-cols-3 gap-1.5">
            {monthsShort.map((label, index) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  setCursor({ ...cursor, m: index + 1, d: Math.min(cursor.d, daysIn(cursor.y, index + 1)) })
                  setMode('days')
                }}
                className={cn(
                  'h-10 rounded-[calc(9px*var(--radius-scale))] text-[length:calc(13px*var(--text-scale))] transition-colors',
                  cursor.m === index + 1 ? 'bg-accent-soft font-semibold text-accent' : 'text-fg hover:bg-hover',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {mode === 'years' && (
          <div className="grid grid-cols-3 gap-1.5">
            {Array.from({ length: 12 }, (_, index) => yearStart + index).map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => {
                  setCursor({ ...cursor, y: year, d: Math.min(cursor.d, daysIn(year, cursor.m)) })
                  setMode('months')
                }}
                className={cn(
                  'num h-10 rounded-[calc(9px*var(--radius-scale))] text-[length:calc(13px*var(--text-scale))] transition-colors',
                  cursor.y === year ? 'bg-accent-soft font-semibold text-accent' : year === today.y ? 'font-semibold text-fg hover:bg-hover' : 'text-fg hover:bg-hover',
                )}
              >
                {year}
              </button>
            ))}
          </div>
        )}
      </div>

      {withTime && (
        <div className="flex items-center justify-between gap-2 border-t border-line pt-2.5">
          <span className="text-[length:calc(12.5px*var(--text-scale))] text-muted">{t('common.datePicker.time')}</span>
          <div className="flex items-center gap-1">
            <TimeBox label={t('common.datePicker.hours')} value={time.hh} max={23} onChange={(hh) => setTime({ hh })} />
            <span className="text-muted">:</span>
            <TimeBox label={t('common.datePicker.minutes')} value={time.mm} max={59} onChange={(mm) => setTime({ mm })} />
          </div>
        </div>
      )}

      <div className="flex items-center gap-1.5 border-t border-line pt-2.5">
        <Button variant="ghost" size="sm" onClick={onClear}>
          {t('common.datePicker.clear')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={() => {
            const now = todayParts()
            if (!inRange(now)) return
            if (withTime) onPick(now)
            else onPick({ ...now, hh: 0, mm: 0 })
            setCursor(now)
            setMode('days')
          }}
        >
          {withTime ? t('common.datePicker.now') : t('common.datePicker.today')}
        </Button>
        {withTime && (
          <Button variant="primary" size="sm" onClick={onDone}>
            {t('common.datePicker.done')}
          </Button>
        )}
      </div>
    </div>
  )
}

/** Two-digit hour/minute box: type digits, or ↑ ↓ to step. */
function TimeBox({ label, value, max, onChange }: { label: string; value: number; max: number; onChange: (value: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null)
  return (
    <input
      aria-label={label}
      inputMode="numeric"
      value={draft ?? pad(value)}
      onFocus={(event) => event.target.select()}
      onChange={(event) => {
        const digits = event.target.value.replace(/\D/g, '').slice(-2)
        setDraft(digits)
        if (digits.length === 2) {
          onChange(Math.min(max, +digits))
          setDraft(null)
        }
      }}
      onBlur={() => {
        if (draft) onChange(Math.min(max, +draft))
        setDraft(null)
      }}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
        event.preventDefault()
        onChange((value + (event.key === 'ArrowUp' ? 1 : -1) + max + 1) % (max + 1))
      }}
      className={cn(controlClass, 'num h-8 w-11 px-0 text-center text-[length:calc(13.5px*var(--text-scale))]')}
    />
  )
}
